import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, LayoutGrid, Layers, Repeat, PlusCircle, Droplets, Waves, ShoppingCart, Wrench, FileText, Presentation } from "lucide-react";
import { PRODUCTS, CATEGORIES, IMAGES } from "../lib/data";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";

const CAT_ICONS = { Droplets, Waves, LayoutGrid };

const PILLARS = [
  { icon: Package, title: "Curated Products", desc: "Best-in-class products we'd stake our name on — never a bloated catalog.", to: "#portfolio" },
  { icon: LayoutGrid, title: "Multiple Categories", desc: "Absorbents, water treatment, and ground protection — clearly organized by category.", to: "#portfolio" },
  { icon: Layers, title: "Sector-Matched", desc: "The right products matched to how your sector actually operates.", to: "/industries" },
  { icon: Repeat, title: "Growing Portfolio", desc: "We add proven products to each category as we expand across sectors.", to: "#portfolio" },
];

import { Seo } from "../components/site/Seo";

export default function Solutions() {
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.categorySlug === active);
  const activeCat = CATEGORIES.find((c) => c.slug === active);
  const gpCategory = CATEGORIES.find((c) => c.slug === "ground-protection");
  // Ground protection is sold as a single-source partnership (purchase or rent), not a single product.
  const showGP = active === "all" || active === "ground-protection";
  const displayProducts = filtered.filter((p) => p.categorySlug !== "ground-protection");

  return (
    <div data-testid="solutions-page">
      <Seo title="Products & Solutions" description="Explore OneHarvest's full lineup — industrial absorbents, water treatment, and ground protection matting engineered for demanding jobsites." />
      <PageHero
        kicker="Solutions"
        title="Integrated solutions, not a shopping cart."
        subtitle="We curate best-in-class products across categories and match them to your sector — so you get exactly the right products, backed by expert guidance."
        image={IMAGES.bulldozer}
      />

      {/* FILTERABLE PORTFOLIO */}
      <section id="portfolio" data-testid="portfolio-section" className="bg-muted scroll-mt-24">
        <div className="container-x py-24">
          <SectionHead kicker="Product Categories" title="The curated portfolio, by category" desc="We don't carry thousands of products. We build focused categories of best-in-class solutions — and add proven products to each as we grow across sectors." />

          {/* Category filter */}
          <div className="mt-12 flex flex-wrap gap-3" data-testid="category-filter">
            <button
              data-testid="filter-all"
              onClick={() => setActive("all")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border transition-colors ${
                active === "all" ? "bg-flame text-white border-flame" : "bg-card text-charcoal border-border hover:border-flame/50"
              }`}
            >
              All Products <span className="opacity-70">({PRODUCTS.length})</span>
            </button>
            {CATEGORIES.map((cat) => {
              const Icon = CAT_ICONS[cat.icon] || Package;
              const count = PRODUCTS.filter((p) => p.categorySlug === cat.slug).length;
              const on = active === cat.slug;
              return (
                <button
                  key={cat.slug}
                  data-testid={`filter-${cat.slug}`}
                  onClick={() => setActive(cat.slug)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border transition-colors ${
                    on ? "bg-flame text-white border-flame" : "bg-card text-charcoal border-border hover:border-flame/50"
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} /> {cat.name} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Active category description */}
          {activeCat && (
            <div data-testid="active-category-desc" className="mt-8 border-l-2 border-flame bg-card p-6 max-w-3xl">
              <div className="font-display font-bold text-xl text-ink">{activeCat.name}</div>
              <p className="mt-2 text-muted-foreground leading-relaxed">{activeCat.desc}</p>
            </div>
          )}

          {/* Product grid */}
          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="product-grid">
            {displayProducts.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.1}>
                <Link to={`/products/${p.slug}`} data-testid={`portfolio-${p.slug}`} className="group relative flex flex-col border border-border bg-card overflow-hidden hover:border-flame/50 hover:shadow-xl transition-all h-full">
                  <div className="absolute top-0 left-0 z-20 h-1 w-0 bg-flame group-hover:w-full transition-all duration-500 ease-out" />
                  <div className={`relative aspect-[4/3] overflow-hidden ${p.fit === "contain" ? "bg-white" : "bg-charcoal"}`}>
                    <img src={p.image} alt={p.name} className={`h-full w-full group-hover:scale-105 transition-transform duration-500 ${p.fit === "contain" ? "object-contain p-3" : "object-cover"}`} />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-ink/85 backdrop-blur-sm text-white px-2.5 py-1 border border-white/10">{p.category}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-2xl text-ink group-hover:text-flame transition-colors">{p.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{p.tagline}</p>
                    {p.useCases && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.useCases.slice(0, 3).map((u) => (
                          <span key={u} className="text-[11px] bg-muted text-charcoal px-2.5 py-1 border border-border">{u}</span>
                        ))}
                      </div>
                    )}
                    <span className="mt-5 inline-flex items-center gap-2 text-flame text-sm font-semibold">
                      View product <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}

            {showGP && gpCategory && (
              <Reveal delay={(displayProducts.length % 3) * 0.1}>
                <Link to="/ground-protection" data-testid="portfolio-single-source-mat-partner" className="group relative flex flex-col border border-border bg-card overflow-hidden hover:border-flame/50 hover:shadow-xl transition-all h-full">
                  <div className="absolute top-0 left-0 z-20 h-1 w-0 bg-flame group-hover:w-full transition-all duration-500 ease-out" />
                  <div className="relative aspect-[4/3] overflow-hidden bg-charcoal">
                    <img src={gpCategory.image} alt="Ground protection matting" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-ink/85 backdrop-blur-sm text-white px-2.5 py-1 border border-white/10">Ground Protection & Site Access</span>
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-flame text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">Single Source</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-2xl text-ink group-hover:text-flame transition-colors">Single Source Mat Partner</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">Any mat, from any manufacturer — purchase or rent. We source, deliver, and support the right matting for your project, including flagship Big Bad Mats when durability matters most.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Purchase", "Rent", "Any mat type"].map((u) => (
                        <span key={u} className="text-[11px] bg-muted text-charcoal px-2.5 py-1 border border-border">{u}</span>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-2 text-flame text-sm font-semibold">
                      Explore ground protection <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            )}
          </div>

          {/* More categories strip */}
          <Reveal>
            <div className="mt-12 border border-dashed border-flame/40 bg-flame/5 p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
              <div className="flex items-center gap-5">
                <PlusCircle className="h-10 w-10 text-flame flex-shrink-0" />
                <div>
                  <h3 className="font-display font-bold text-xl text-ink">More categories & products are on the way.</h3>
                  <p className="text-muted-foreground mt-1">As we expand across sectors, we add proven products and new categories — always keeping our curated standard.</p>
                </div>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-ink text-ink px-5 py-3 font-semibold hover:bg-ink hover:text-white transition-colors whitespace-nowrap">
                Suggest a product
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GROUND PROTECTION — TWO WAYS TO DEPLOY */}
      <section className="bg-ink text-white" data-testid="ground-protection-avenues">
        <div className="container-x py-24">
          <Reveal>
            <div className="max-w-2xl">
              <div className="kicker text-flame mb-3">Ground Protection · Single Source Mat Partner · Purchase or Rent</div>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Your single source mat partner — purchase or rent.</h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                We're your single source for ground protection and site access. We source <span className="text-white font-semibold">whatever mat fits your project</span> — any type, any manufacturer, including flagship
                <span className="text-white font-semibold"> Big Bad Mats</span> when durability matters most. Then choose how to get it: <span className="text-white font-semibold">purchase to own</span>, or <span className="text-white font-semibold">rent it turnkey.</span>
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {[
              { key: "buy", icon: ShoppingCart, title: "Purchase", tag: "Own the mats · you deploy & reuse",
                desc: "Buy whatever mat makes the most sense for your project — any type, any manufacturer, flagship Big Bad Mats — and own a reusable fleet you control. Best for recurring, in-house use at the lowest lifecycle cost.",
                cta: "Get a purchase quote", to: "/request-quote?avenue=buy" },
              { key: "rent", icon: Repeat, title: "Rent", tag: "We deliver, install & remove — turnkey",
                desc: "Prefer to hand off the whole job? We source, deliver, install, maintain, and remove the right matting for your project or your client's — rental included. No capital purchase, no logistics.",
                cta: "Request a rental quote", to: "/request-quote?avenue=rent" },
            ].map((c) => (
              <Reveal key={c.key}>
                <div data-testid={`gp-avenue-${c.key}`} className="group h-full border border-white/10 bg-white/[0.02] p-8 flex flex-col hover:border-flame/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-flame/10 border border-flame/25 text-flame flex items-center justify-center group-hover:bg-flame group-hover:text-white transition-colors">
                      <c.icon className="h-6 w-6" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-flame">{c.tag}</div>
                      <h3 className="font-display font-bold text-2xl">{c.title}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-white/60 leading-relaxed flex-1">{c.desc}</p>
                  <Link to={c.to} data-testid={`gp-cta-${c.key}`} className="mt-6 inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-3.5 font-semibold hover:bg-flame/90 transition-colors">
                    {c.cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="gp-tools">
            <Link to="/ground-protection" data-testid="gp-catalog-link"
              className="group inline-flex items-center justify-between gap-3 border border-white/15 bg-white/[0.02] px-6 py-4 hover:border-flame transition-colors">
              <span>
                <span className="block font-display font-bold text-white">Browse the Mat Catalog</span>
                <span className="block text-sm text-white/50">Timber · Composite · Steel · Rubber</span>
              </span>
              <ArrowRight className="h-5 w-5 text-flame group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/mat-selector" data-testid="gp-selector-link"
              className="group inline-flex items-center justify-between gap-3 border border-flame/40 bg-flame/10 px-6 py-4 hover:bg-flame/20 transition-colors">
              <span>
                <span className="block font-display font-bold text-white">Not sure? Find my mat</span>
                <span className="block text-sm text-white/60">8-question guided selector</span>
              </span>
              <ArrowRight className="h-5 w-5 text-flame group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <span className="text-white/40 uppercase tracking-widest text-[11px]">Learn more</span>
            <Link to="/ground-protection-flyer" data-testid="gp-flyer-link" className="inline-flex items-center gap-2 text-white/70 hover:text-flame transition-colors">
              <FileText className="h-4 w-4" /> One-page flyer
            </Link>
            <Link to="/ground-protection-deck" data-testid="gp-deck-link" className="inline-flex items-center gap-2 text-white/70 hover:text-flame transition-colors">
              <Presentation className="h-4 w-4" /> Purchase vs. Rent deck
            </Link>
          </div>
        </div>
      </section>

      {/* LAND STEWARDSHIP CALLOUT */}
      <section className="bg-background border-t border-border" data-testid="solutions-stewardship-callout">
        <div className="container-x py-16">
          <Reveal>
            <div className="border border-border bg-card p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div className="max-w-2xl">
                <div className="kicker text-flame mb-3">More Than Products · A Preservation Solution</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">Every solution here also protects the land as a long-cycle asset.</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  BoneDry, Pinkwater, and Big Bad Mats keep soil, water, and topsoil clean and intact — preserving value for future agriculture and livestock, and giving operators visionary stewardship leadership.
                </p>
              </div>
              <Link to="/land-stewardship" data-testid="solutions-stewardship-cta" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors whitespace-nowrap flex-shrink-0">
                See our stewardship approach <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
